import React from 'react';
import { connect } from 'react-redux';
import { Impression } from '@nerdwallet/analytics/react/impression';
import { Num as num } from '@nerdwallet/nw-client-lib';
import lazy, { Hydrate } from '../../../lazy/lazy-universal';

import type { LoanTerm } from '../../../amortization-calculator/types';
import { MORTGAGE_ANALYTICS_HASH } from '../analytics-constants';
import type { MortgageCalculators } from '../types';
import { formatResultForSegment } from '../../../../lib/analytics/format-result-for-segment';
import SegmentImpression, {
  useSegmentImpressionContext,
} from '../../../../lib/analytics/segment-impression';

// Lazy Load Component
const MonthlyResults = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-monthly-results' */ '../../../currency/monthly-results/monthly-results'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

interface MCMonthlyResultsProps {
  mortgageCalculator: MortgageCalculators;
}

interface StateProps {
  /** derived from redux */
  loanTerm?: LoanTerm;
  /** derived from redux */
  monthlyPaymentsTotal?: number;
  /** derived from redux */
  totalInterestPaid?: number;
}

type OwnProps = MCMonthlyResultsProps & StateProps;

const MCMonthlyResults = ({
  monthlyPaymentsTotal,
  loanTerm,
  totalInterestPaid,
  mortgageCalculator,
}: OwnProps): JSX.Element => {
  const headerText = 'Your monthly payment';
  const displayValue: number = num.formatCurrency(monthlyPaymentsTotal);

  const ANALYTICS =
    MORTGAGE_ANALYTICS_HASH[mortgageCalculator].RESULTS_HEADER_FORM;
  const { eventId: formViewedId } = useSegmentImpressionContext() || {};

  return (
    <Impression
      eventType={ANALYTICS.EVENT_TYPE}
      entityName={ANALYTICS.ENTITY_NAME}
      payload={{
        form_name: ANALYTICS.FORM_NAME,
        section_name: ANALYTICS.SECTION_NAME,
      }}
    >
      <SegmentImpression
        enabled={mortgageCalculator === 'mc' || mortgageCalculator === 'mpc'}
        eventType="feature_viewed"
        attributes={{
          entity_name: 'form_result_text',
          section_name: 'form',
          item_position: null,
          item_copy:
            mortgageCalculator === 'mc'
              ? 'mortgage_calculator'
              : 'mortgage_payment_calculator',
          content_id: null,
          additional_attributes: formatResultForSegment([
            { label: headerText, displayValue },
            {
              label: 'Your Total Interest Paid',
              displayValue: num.formatCurrency(totalInterestPaid),
            },
          ]),
          parent_event_id: formViewedId,
        }}
      >
        <MonthlyResults
          headerText={headerText}
          loanTerm={loanTerm}
          total={displayValue}
          mortgageCalculator={mortgageCalculator}
        />
      </SegmentImpression>
    </Impression>
  );
};

const mapStateToProps = (state) => {
  const reducer = state?.mortgageCalculator;

  return {
    loanTerm: reducer?.inputs?.loanTerm,
    monthlyPaymentsTotal: reducer?.outputs?.monthlyPayments?.total,
    totalInterestPaid: reducer?.outputs?.totalInterestPaid,
  };
};

export default connect<
  StateProps,
  Record<string, never>,
  MCMonthlyResultsProps
>(mapStateToProps)(MCMonthlyResults);
